import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { COMMUNICATION_SKILLS_LESSON_NO } from "../../services/api/canDo";
import { fetchCanDosBundle, type CanDoItem, type CommunicationTopicMeta } from "../../modules/tutor/api/tutor.client";
import {
  createCommunicationAssignment,
  listTeacherCommunicationAssignments,
} from "../../services/api/communicationLearning";
import { ROUTES } from "../../utils/constants";
import { showErrorToast, showSuccessToast } from "../../utils/errorHandler";

type Props = {
  /** From teacher classes — may include `{ value: "", label: "Select grade..." }` */
  gradeSelectOptions: { value: string; label: string }[];
  sectionSelectOptions: { value: string; label: string }[];
};

export function TeacherCommunicationPracticeTasksPanel({
  gradeSelectOptions,
  sectionSelectOptions,
}: Props) {
  const numericGradeOptions = useMemo(
    () => gradeSelectOptions.filter((o) => o.value !== ""),
    [gradeSelectOptions]
  );

  const [grade, setGrade] = useState<number>(() => {
    const first = numericGradeOptions[0]?.value;
    const n = first ? Number(first) : NaN;
    return Number.isFinite(n) && n >= 1 ? n : 3;
  });

  useEffect(() => {
    if (numericGradeOptions.length === 0) return;
    const allowed = new Set(numericGradeOptions.map((o) => Number(o.value)));
    if (!allowed.has(grade)) {
      const v = Number(numericGradeOptions[0].value);
      if (Number.isFinite(v)) setGrade(v);
    }
  }, [numericGradeOptions, grade]);

  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [topics, setTopics] = useState<CommunicationTopicMeta[]>([]);
  const [canDos, setCanDos] = useState<CanDoItem[]>([]);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [topicId, setTopicId] = useState("");
  const [requiredIds, setRequiredIds] = useState<Set<string>>(new Set());
  const [sectionId, setSectionId] = useState("");
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const r = await listTeacherCommunicationAssignments();
      setItems((r.items as Record<string, unknown>[]) ?? []);
    } catch {
      setItems([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadBundle = useCallback(async () => {
    setBundleLoading(true);
    try {
      const { can_dos, communication_topics } = await fetchCanDosBundle(grade, COMMUNICATION_SKILLS_LESSON_NO);
      setTopics(communication_topics ?? []);
      setCanDos(can_dos ?? []);
      setTopicId("");
      setRequiredIds(new Set());
    } catch {
      setTopics([]);
      setCanDos([]);
    } finally {
      setBundleLoading(false);
    }
  }, [grade]);

  useEffect(() => {
    void loadBundle();
  }, [loadBundle]);

  const canDosForTopic = useMemo(() => {
    if (!topicId.trim()) return [];
    return canDos.filter((c) => (c.communication_topic_id || "") === topicId);
  }, [canDos, topicId]);

  const toggleRequired = (id: string) => {
    setRequiredIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const submit = async () => {
    if (!title.trim() || !due.trim() || !topicId.trim()) {
      showErrorToast("Title, due date, and communication topic are required.");
      return;
    }
    setBusy(true);
    try {
      await createCommunicationAssignment({
        title: title.trim(),
        grade,
        due_date: due.trim(),
        min_attempts: 1,
        target_rubric_level: "L3",
        topic_id: topicId.trim(),
        required_can_do_ids: Array.from(requiredIds),
        section_id: sectionId.trim() || undefined,
        student_ids: [],
      });
      showSuccessToast(
        "Practice task created. Students see it under My Assignments (Communication practice) and a reminder in Communication Tutor."
      );
      setTitle("");
      setRequiredIds(new Set());
      await loadList();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : "Failed to create.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Topics and can-dos come from{" "}
        <Link to={ROUTES.TEACHER_CAN_DO} className="text-primary-600 font-semibold underline">
          Lesson Can Do → Communication
        </Link>
        . Pick a topic and optional required can-dos below.
      </p>

      <Card className="border border-purple-100 shadow-md bg-gradient-to-br from-white to-violet-50/40 dark:from-slate-900 dark:to-violet-950/20">
        <CardHeader>
          <CardTitle className="text-base text-purple-900 dark:text-purple-200">New practice task</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="Title (e.g. Practice greeting at the shop)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="flex flex-wrap gap-3">
            {numericGradeOptions.length > 0 ? (
              <div className="flex-1 min-w-[160px]">
                <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Grade</label>
                <Select
                  options={numericGradeOptions}
                  value={String(grade)}
                  onChange={(e) => setGrade(Number(e.target.value))}
                />
              </div>
            ) : (
              <label className="text-sm">
                Grade
                <input
                  type="number"
                  min={1}
                  max={12}
                  className="block mt-1 w-24 px-2 py-1 rounded border border-slate-200 dark:border-slate-600 dark:bg-slate-800"
                  value={grade}
                  onChange={(e) => setGrade(Math.min(12, Math.max(1, Number(e.target.value) || 1)))}
                />
              </label>
            )}
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Due</label>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Section (optional)</label>
            <Select
              options={sectionSelectOptions}
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">
              Communication topic
              {bundleLoading ? (
                <span className="ml-2 text-xs font-normal text-slate-500">Loading…</span>
              ) : null}
            </label>
            <Select
              options={[
                { value: "", label: topics.length ? "Select topic…" : "No topics — add them in Lesson Can Do" },
                ...topics.map((t) => ({ value: t.id, label: (t.title_en || "").trim() || t.id })),
              ]}
              value={topicId}
              onChange={(e) => {
                setTopicId(e.target.value);
                setRequiredIds(new Set());
              }}
            />
          </div>
          {topicId ? (
            <div className="border border-slate-200 dark:border-slate-600 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                Required can-dos (optional)
              </p>
              {canDosForTopic.length === 0 ? (
                <p className="text-xs text-slate-500">No can-dos under this topic yet.</p>
              ) : (
                <ul className="max-h-40 overflow-y-auto space-y-2">
                  {canDosForTopic.map((c) => (
                    <li key={c.id}>
                      <label className="flex gap-2 text-sm cursor-pointer items-start">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={requiredIds.has(c.id)}
                          onChange={() => toggleRequired(c.id)}
                        />
                        <span>{c.statement_en}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
          <Button type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? "Saving…" : "Create practice task"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your practice tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500">No tasks yet.</p>
          ) : (
            <ul className="space-y-2">
              {items.map((row) => {
                const a = row as Record<string, unknown>;
                const id = String(a._id ?? "");
                const tids = (a.required_can_do_ids as string[]) || [];
                return (
                  <li key={id} className="text-sm border-b border-slate-100 dark:border-slate-700 pb-2">
                    <span className="font-medium text-slate-900 dark:text-white">{String(a.title ?? "")}</span>
                    <span className="text-slate-500 dark:text-slate-400"> · Grade {String(a.grade ?? "")}</span>
                    <span className="text-slate-500 dark:text-slate-400"> · Due {String(a.due_date ?? "")}</span>
                    {a.topic_id ? (
                      <span className="text-slate-500 dark:text-slate-400"> · Topic linked</span>
                    ) : null}
                    {tids.length ? (
                      <span className="text-slate-500 dark:text-slate-400"> · {tids.length} required can-do(s)</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
